import React, { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Circle, Polygon } from '@react-google-maps/api';
import { getGoogleMapsConfig } from '../../utils/mapsConfig';

// Map container style
const containerStyle = {
  width: '100%',
  height: '100%'
};

// Default center (New York City)
const defaultCenter = {
  lat: 40.7128,
  lng: -74.006
};

// Map options
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  mapTypeId: 'roadmap'
};

const GeofenceMap = ({ geofence, vehicles = [], editable = false, onChange }) => {
  const [map, setMap] = useState(null);
  
  // Load Google Maps script with shared config
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsConfig());
  
  // Handle map load
  const onLoad = useCallback((map) => {
    setMap(map);
    
    // Center map on geofence
    if (geofence) {
      if (geofence.type === 'circle' && geofence.center) {
        map.setCenter(geofence.center);
        map.setZoom(13);
      } else if (geofence.type === 'polygon' && geofence.vertices && geofence.vertices.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        geofence.vertices.forEach(vertex => bounds.extend(vertex));
        map.fitBounds(bounds);
      }
    }
  }, [geofence]);
  
  // Handle map unmount
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);
  
  // Handle circle change
  const onCircleComplete = useCallback((circle) => {
    if (onChange) {
      onChange({
        type: 'circle',
        center: {
          lat: circle.getCenter().lat(),
          lng: circle.getCenter().lng()
        },
        radius: circle.getRadius()
      });
    }
  }, [onChange]);
  
  // Handle polygon change
  const onPolygonComplete = useCallback((polygon) => {
    if (onChange) {
      const vertices = polygon.getPath().getArray().map(latLng => ({
        lat: latLng.lat(),
        lng: latLng.lng()
      }));
      
      onChange({
        type: 'polygon',
        vertices
      });
    }
  }, [onChange]);
  
  // Circle options
  const circleOptions = {
    fillColor: '#1A5F7A',
    fillOpacity: 0.2,
    strokeColor: '#1A5F7A',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    editable: editable,
    draggable: editable
  };
  
  // Polygon options
  const polygonOptions = {
    fillColor: '#1A5F7A',
    fillOpacity: 0.2,
    strokeColor: '#1A5F7A',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    editable: editable,
    draggable: editable
  };
  
  // Render loading message
  if (loadError) {
    return <div className="text-ui-error p-4">Error loading maps</div>;
  }
  
  if (!isLoaded) {
    return <div className="flex justify-center items-center h-full">Loading maps...</div>;
  }
  
  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={geofence?.type === 'circle' ? geofence.center : defaultCenter}
      zoom={12}
      options={mapOptions}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {/* Circle Geofence */}
      {geofence && geofence.type === 'circle' && (
        <Circle
          center={geofence.center}
          radius={geofence.radius}
          options={circleOptions}
          onRadiusChanged={() => {
            if (editable && onChange && map) {
              const circles = map.overlays?.getArray().filter(o => o instanceof window.google.maps.Circle);
              if (circles && circles.length > 0) {
                const circle = circles[0];
                onChange({
                  type: 'circle',
                  center: {
                    lat: circle.getCenter().lat(),
                    lng: circle.getCenter().lng()
                  },
                  radius: circle.getRadius()
                });
              }
            }
          }}
          onCenterChanged={() => {
            if (editable && onChange && map) {
              const circles = map.overlays?.getArray().filter(o => o instanceof window.google.maps.Circle);
              if (circles && circles.length > 0) {
                const circle = circles[0];
                onChange({
                  type: 'circle',
                  center: {
                    lat: circle.getCenter().lat(),
                    lng: circle.getCenter().lng()
                  },
                  radius: circle.getRadius()
                });
              }
            }
          }}
        />
      )}
      
      {/* Polygon Geofence */}
      {geofence && geofence.type === 'polygon' && geofence.vertices && (
        <Polygon
          paths={geofence.vertices}
          options={polygonOptions}
          onMouseUp={() => {
            if (editable && onChange && map) {
              const polygons = map.overlays?.getArray().filter(o => o instanceof window.google.maps.Polygon);
              if (polygons && polygons.length > 0) {
                const polygon = polygons[0];
                const vertices = polygon.getPath().getArray().map(latLng => ({
                  lat: latLng.lat(),
                  lng: latLng.lng()
                }));
                
                onChange({
                  type: 'polygon',
                  vertices
                });
              }
            }
          }}
        />
      )}
    </GoogleMap>
  );
};

export default GeofenceMap;